import React from "react";
import { Link, useNavigate } from 'react-router-dom';
import { styled, useTheme } from "@mui/material/styles";
import { Divider, Box, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Drawer as MuiDrawer, IconButton } from "@mui/material";
import {
    ChevronRight as ChevronRightIcon,
    ChevronLeft as ChevronLeftIcon,
    ShieldOutlined as ShieldOutlinedIcon,
    GroupOutlined as GroupOutlinedIcon,
    DashboardOutlined as DashboardOutlinedIcon,
    PowerOutlined as PowerOutlinedIcon,
    SettingsOutlined as SettingsOutlinedIcon,
    Logout as LogoutIcon,
    CalendarMonthOutlined as CalendarMonthOutlinedIcon,
    WorkspacesOutlined as WorkspacesOutlinedIcon,
    ColorLensOutlined as ColorLensOutlinedIcon,
    ArchiveOutlined as ArchiveOutlinedIcon,
    WorkHistoryOutlined as WorkHistoryOutlinedIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from "react-redux";
import { handleLogout as doLogout } from "../../Modules/Common/Auth";
import { setActiveMenu, setLeftDrawer } from "../../Modules/Common/App";
import {usePermissions} from "../../Hooks/usePermissions";

const drawerWidth = 240;

const openedMixin = (theme) => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
    '& .MuiList-root': {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        height: '100%',
    }
});

const closedMixin = (theme) => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
    [theme.breakpoints.down('sm')]: {
        width: theme.spacing(0),
    },
});

const StyledDrawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open }) => ({
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        ...(open && {
            ...openedMixin(theme),
            '& .MuiDrawer-paper': openedMixin(theme),
        }),
        ...(!open && {
            ...closedMixin(theme),
            '& .MuiDrawer-paper': closedMixin(theme),
        }),
        '& .drawer-header': {
            justifyContent: 'space-between',
            display: 'flex',
            alignItems: 'center',
            padding: theme.spacing(0, 1),
            ...theme.mixins.toolbar,
        }
    }),
);

const StyledListItem = styled(ListItem)(({ theme, selected, open }) => ({
    display: 'flex',
    '&.Mui-selected': {
        backgroundColor: theme.palette.secondary.light,
        '& .MuiListItemText-root': {
            color: theme.palette.primary.light,
        },
        '& .MuiListItemIcon-root': {
            color: theme.palette.primary.light,
        }
    },
    '& .MuiListItemText-root': {
        color: selected ? theme.palette.primary.light : theme.palette.secondary.dark,
    },
    '& .MuiListItemButton-root': {
        padding: theme.spacing(1, 2.5),
        minHeight: 60,
        justifyContent: open ? 'initial' : 'center',
        '&:hover .MuiListItemText-primary': {
            color: theme.palette.primary.main,
        }
    },
    '& .MuiListItem-root:hover': {
        '& .MuiListItemText-root': {
            color: theme.palette.primary.main,
        },
        '& .MuiListItemIcon-root': {
            color: theme.palette.primary.main,
        },
    }
}));

const LeftDrawer = () => {

    const dispatch = useDispatch();
    const app = useSelector((store) => store.app);
    const user = useSelector((store) => store.auth.user);
    const {hasPermission} = usePermissions();
    const theme = useTheme();
    const navigate = useNavigate();

    const menuItems = (app) => {

        const menuArray = [
            { permission: 'core.dashboard.menu', name: 'Dashboard', icon: <DashboardOutlinedIcon />, link: '/dashboard', order: 1, default: true, iconColor: theme.palette.primary.main },
            { permission: 'roster.roster.menu', name: 'Roster', icon: <CalendarMonthOutlinedIcon />, link: '/roster', order: 2, default: true, iconColor: theme.palette.primary.main },
            {
                permission: 'roster.department.menu',
                name: 'Departments',
                icon: <WorkspacesOutlinedIcon />,
                iconColor: theme.palette.primary.main,
                link: user?.role_type && ['MANAGER', 'EMPLOYEE'].includes(user.role_type) && user?.department_id
                    ? `/departments/${user.department_id}`
                    : '/departments',
                order: 3,
                default: true
            },
            { permission: 'roster.archive.menu', name: 'Archive', icon: <ArchiveOutlinedIcon />, link: '/roster/archive', order: 4, default: true, iconColor: theme.palette.primary.main },
            { permission: 'roster.jobs.menu', name: 'Jobs', icon: <WorkHistoryOutlinedIcon />, link: '/roster/jobs', order: 5, default: true, iconColor: theme.palette.primary.main },
            { permission: 'core.user.menu', name: 'Users', icon: <GroupOutlinedIcon />, link: '/users', order: 21, system: false, iconColor: theme.palette.primary.main },
            { permission: 'core.permission.menu', name: 'Permissions', icon: <PowerOutlinedIcon />, link: '/permissions', order: 18, system: true, iconColor: theme.palette.primary.main },
            { permission: 'core.role.menu', name: 'Roles', icon: <ShieldOutlinedIcon />, link: '/roles', order: 20, system: false, iconColor: theme.palette.primary.main },
            { permission: 'core.theme.menu', name: 'Themes', icon: <ColorLensOutlinedIcon />, link: '/themes', order: 24, system: false, iconColor: theme.palette.primary.main },
            { permission: 'core.settings.menu', name: 'Settings', icon: <SettingsOutlinedIcon />, link: '/settings', order: 26, system: false, iconColor: theme.palette.error.main },
        ]
        return menuArray.sort((a, b) => a.order - b.order);
    };

    const handleLogout = () => {
        doLogout(dispatch).then(() => {
            navigate('/login');
        });
    };

    const setMenu = (menu) => {
        dispatch(setActiveMenu(menu));
    };

    const handleLink = (item) => {
        navigate(item.link);
    };

    const open = app.leftDrawer.open;

    return (
        <StyledDrawer variant="permanent" open={open}>
            <Box className="drawer-header">
                <Link to="/">
                    <Box component="img" src={'/images/logo.png'} alt="Logo" sx={{ height: '44px', marginRight: '16px' }} />
                </Link>
                <IconButton onClick={() => dispatch(setLeftDrawer({ component: null, open: false }))}>
                    {theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                </IconButton>
            </Box>
            <Divider />
            <List sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                padding: 0
            }}>
                <Box sx={{ flex: 1, width: '100%' }}>
                    {menuItems(app)
                        .filter(item => hasPermission(item.permission))
                        .map(item => (
                            <StyledListItem
                                divider
                                key={item.name}
                                disablePadding
                                onClick={() => handleLink(item)}
                            >
                                <ListItemButton onClick={() => setMenu(item.name)}>
                                    <ListItemIcon
                                        sx={{
                                            minWidth: 0,
                                            mr: open ? 3 : 'auto',
                                            justifyContent: 'center',
                                            color: item.iconColor || theme.palette.secondary.main
                                        }}
                                    >
                                        {item.icon}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={item.name}
                                        primaryTypographyProps={{
                                            sx: { color: item.iconColor || theme.palette.secondary.main }
                                        }}
                                        sx={{ opacity: open ? 1 : 0 }}
                                    />
                                </ListItemButton>
                            </StyledListItem>
                        ))
                    }
                </Box>

                <ListItem key='logout' disablePadding sx={{
                    display: 'flex',
                    width: '100%',
                    borderTop: '1px solid',
                    borderColor: 'divider'
                }} onClick={handleLogout}>
                    <ListItemButton sx={{
                        minHeight: 48,
                        justifyContent: open ? 'initial' : 'center',
                        px: 2.5,
                        width: '100%'
                    }}>
                        <ListItemIcon sx={{
                            minWidth: 0,
                            mr: open ? 3 : 'auto',
                            justifyContent: 'center',
                            color: theme.palette.error.main
                        }}>
                            <LogoutIcon />
                        </ListItemIcon>
                        <ListItemText
                            primary='Logout'
                            sx={{
                                opacity: open ? 1 : 0,
                                color: theme.palette.error.main
                            }}
                        />
                    </ListItemButton>
                </ListItem>
            </List>
        </StyledDrawer>
    );
};

export default LeftDrawer;
